import axios from 'axios';

// 建立專門用於推薦信公開 API 的 axios 實例
const recommendationClient = axios.create({
  baseURL: '', // 將在請求攔截器中動態設定
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 請求攔截器
recommendationClient.interceptors.request.use(
  (config) => {
    // 動態引入 store 以避免循環依賴
    const store = require('@/store').default;
    
    // 從 store 動態設定 baseURL
    if (!config.baseURL || config.baseURL === '') {
      config.baseURL = store.state.src + store.state.subDirectory;
    }
    
    // 公開 API 不需要 JWT token
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 回應攔截器
recommendationClient.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const recommendationService = {
  /**
   * 取得推薦信資訊
   * @param {string} linkCode - 推薦信邀請連結代碼
   * @returns {Promise} 推薦信詳細資訊
   */
  async getRecommendationInfo(linkCode) {
    try {
      const response = await recommendationClient.get(
        `/ApplyStudent/Public/GetRecommendationInfo/${linkCode}`
      );
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      let message = 'Failed to fetch recommendation information';
      if (error.response) {
        message = error.response.data.message || message;
      }
      return {
        success: false,
        message: message,
      };
    }
  },

  /**
   * 提交推薦信
   * @param {object} data - 推薦信資料
   * @param {string} data.linkCode - 推薦信邀請連結代碼
   * @param {number} data.agreeType - 同意類型
   * @param {number} data.recommendedMethod - 推薦方式 (1: 線上填寫, 2: 上傳檔案)
   * @param {string} data.recommendedContent - 推薦內容 (選填)
   * @param {string} data.recommendedFile - 推薦信檔案路徑 (選填)
   * @returns {Promise} 提交結果
   */
  async submitRecommendation(data) {
    try {
      const response = await recommendationClient.put(
        '/ApplyStudent/Public/SubmitRecommendation',
        data
      );
      return {
        success: true,
        data: response.data,
        message: response.data.message || 'Recommendation letter submitted successfully',
      };
    } catch (error) {
      let message = 'Failed to submit recommendation letter';
      if (error.response) {
        message = error.response.data.message || message;
      }
      return {
        success: false,
        message: message,
      };
    }
  },

  /**
   * 上傳推薦信檔案
   * @param {string} linkCode - 推薦信邀請連結代碼
   * @param {File} file - 要上傳的檔案
   * @returns {Promise} 上傳結果，包含檔案路徑和預覽 URL
   */
  async uploadRecommendationFile(linkCode, file) {
    try {
      // 將檔案轉換為 Base64
      const base64Data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const response = await recommendationClient.post(
        '/ApplyStudent/Public/UploadRecommendationFile',
        {
          linkCode: linkCode,
          fileName: file.name,
          base64Data: base64Data
        }
      );
      
      return {
        success: true,
        fileUrl: response.data.fileUrl,
        filePreviewUrl: response.data.filePreviewUrl,
        message: response.data.message || 'File uploaded successfully',
      };
    } catch (error) {
      let message = 'Failed to upload file';
      if (error.response && error.response.data) {
        message = error.response.data.message || message;
      }
      return {
        success: false,
        message: message,
      };
    }
  },
};

export default recommendationService;
